// Admin-only Seat Referral Champions widget.
// Ranks users by attributed MRR from invitees who later subscribed on their own.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, TrendingUp, Crown } from "lucide-react";

export default function SeatReferralFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/seat-referral-funnel")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const id = setInterval(() => {
      api.get("/admin/seat-referral-funnel").then((r) => setData(r.data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 mt-8" data-testid="seat-referral-funnel-loading">
        <div className="h-4 w-48 bg-zinc-200 rounded animate-pulse" />
      </div>
    );
  }

  const { champions, total_attributed_mrr_usd, total_conversions } = data;
  const topChamp = champions[0];

  return (
    <div className="bg-white border rounded-lg p-6 mt-8" data-testid="seat-referral-funnel-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
          <Crown size={18} className="text-amber-500" />
          Seat-Referral Champions
        </h2>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">
          Auto-refresh · 60s
        </span>
      </div>
      <p className="text-xs text-zinc-500 -mt-2 mb-4">
        Owners whose Teams invitees later upgraded to Pro/Hustler/Teams on their own credit card.
        This is your highest-quality viral channel — every conversion is a real wholesaler with intent.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="seat-referral-stat-conversions">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-500">
            <Users size={12} className="text-emerald-600" /> Total conversions
          </div>
          <div className="text-2xl font-bold text-zinc-900 mt-1 tabular-nums">{total_conversions}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="seat-referral-stat-mrr">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-emerald-700">
            <TrendingUp size={12} /> Attributed MRR
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums">
            ${total_attributed_mrr_usd}<span className="text-xs font-normal">/mo</span>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="seat-referral-stat-top">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-amber-700">
            <Crown size={12} /> Top champion
          </div>
          <div className="text-base font-bold text-amber-900 mt-1 truncate">
            {topChamp ? (topChamp.name || topChamp.email.split("@")[0]) : "—"}
          </div>
          <div className="text-[11px] text-amber-700">
            {topChamp ? `$${topChamp.mrr_attributed_usd}/mo · ${topChamp.referrals_converted} converts` : "No champions yet"}
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="seat-referral-champions-table">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">#</th>
              <th className="text-left px-4 py-2 font-semibold">Champion</th>
              <th className="text-right px-4 py-2 font-semibold">Conversions</th>
              <th className="text-right px-4 py-2 font-semibold">MRR Driven</th>
            </tr>
          </thead>
          <tbody>
            {champions.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-3 text-zinc-400 italic">
                No referrals have converted yet. Invite teammates from <strong>Settings → Team</strong> to start the flywheel.
              </td></tr>
            ) : (
              champions.map((c, i) => (
                <tr key={c.inviter_user_id} className="border-t border-zinc-100" data-testid={`champ-row-${c.inviter_user_id}`}>
                  <td className="px-4 py-2 text-zinc-500 tabular-nums font-bold">
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                  </td>
                  <td className="px-4 py-2 text-zinc-900">
                    <div className="font-semibold">{c.name || c.email.split("@")[0]}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{c.email}</div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{c.referrals_converted}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-emerald-700">
                    ${c.mrr_attributed_usd}/mo
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
